import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "ja" ? "en" : "ja");
  };

  const handleHashScroll = (hash: string) => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
  };

  const navLinks = [
    { label: t("nav.home"), hash: "#hero" },
    { label: t("nav.curriculum"), hash: "#curriculum" },
    { label: t("nav.instructors"), hash: "#instructors" },
    { label: t("nav.pricing"), hash: "#pricing" },
    { label: t("nav.contact"), hash: "#contact" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-deepblack/90 backdrop-blur-md border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-marble font-serif text-lg md:text-xl tracking-wide">
              Maison Sound
            </span>
            <span className="hidden sm:inline-block px-2.5 py-0.5 border border-champagne/30 rounded-full text-champagne/80 text-[10px] font-sans tracking-widest uppercase">
              {t("hero.tag")}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.hash}
                onClick={() => handleHashScroll(link.hash)}
                className="text-marble/70 hover:text-marble text-sm font-sans tracking-widest uppercase transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleLang}
              className="text-marble/60 hover:text-marble text-xs font-sans tracking-widest uppercase transition-colors duration-300 cursor-pointer"
            >
              {i18n.language === "ja" ? "EN" : "JP"}
            </button>
            <Link
              to="/inquiry"
              className="bg-champagne hover:bg-champagne/90 text-deepblack px-5 py-2 rounded-full text-xs font-sans tracking-widest uppercase transition-all duration-300 whitespace-nowrap"
            >
              {t("cta.button")}
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-marble w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-deepblack/95 backdrop-blur-md border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.hash}
                onClick={() => {
                  handleHashScroll(link.hash);
                  setMobileOpen(false);
                }}
                className="text-marble/70 hover:text-marble text-sm font-sans tracking-widest uppercase transition-colors duration-300 text-left cursor-pointer"
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <button
                onClick={toggleLang}
                className="text-marble/60 hover:text-marble text-xs font-sans tracking-widest uppercase cursor-pointer"
              >
                {i18n.language === "ja" ? "EN" : "JP"}
              </button>
              <Link
                to="/inquiry"
                onClick={() => setMobileOpen(false)}
                className="bg-champagne text-deepblack px-5 py-2 rounded-full text-xs font-sans tracking-widest uppercase"
              >
                {t("cta.button")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}